import React, { Dispatch, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios, { AxiosInstance, AxiosRequestHeaders, Method } from 'axios';
import {
	LazyQueryHookOptions,
	MutationHookOptions,
	QueryHookOptions,
	useLazyQuery as useLazyQueryApollo,
	useMutation as useMutationApollo,
	useQuery as useQueryApollo,
} from '@apollo/client';
import { AppDomain, Errors, useDataReturn } from '../types';
import { AuthTokens, useAuthRequest, useAuthToken } from './useAuthRequest';
import _set from 'lodash/set';
import { DataContext, DataContextEmittedValues, decorateHeaders } from '../components/DataContext';

export interface RestArgs<Response = Record<string, any>> {
	context?: DataContextEmittedValues;
	path: string;
	data?: Record<string, any>;
	queryString?: Record<string, string>;
	method?: string;
	jwt?: boolean;
	domain?: string;
	domains?: Record<string, AppDomain>;
	xAPIKey?: string;
	onData?: (data: Response, args: RestArgs) => void;
	onSuccess?: (data: Response) => void;
	onError?: (err: Errors) => void;
	setData?: Dispatch<any>;
	setError?: Dispatch<Errors>;
	headers?: AxiosRequestHeaders;
}

function BaseUseRestRequest(args: RestArgs): useDataReturn {
	const { apiVersion = 1, restClient } = args && args.context ? args.context : useContext(DataContext);
	const { set, get } = useAuthToken();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Errors>();
	const [data, setData] = useState<any>();

	const request = useCallback((override?: RestArgs) => {
		const merged = {
			...args,
			...(override || {}),
			headers: { ...args.headers, ...(override ? override.headers : {}) },
			data: { ...args.data, ...(override ? override.data : {}) },
			queryString: { ...args.queryString, ...(override ? override.queryString : {}) },
			setData,
			setError,
		} as any;
		const {
			path,
			domain,
			domains,
			data: body = {},
			queryString = {},
			method = 'POST',
			onData,
			onSuccess,
			onError,
			headers = {},
		} = merged;
		setLoading(true);
		setData(undefined);
		setError(undefined);
		return new Promise((resolve, reject) => {
			const newHeaders = decorateHeaders(headers);
			if (merged.jwt) {
				newHeaders['has-jwt'] = 'true';
			}
			if (merged.xAPIKey) {
				newHeaders['x-api-key'] = merged.xAPIKey;
			}
			restClient!
				.request({
					method: method as Method,
					url: `${domains[domain].base}${path.replace(`{ver}`, `v${apiVersion}`)}`,
					data: body,
					params: queryString,
					headers: newHeaders,
				})
				.then((res) => {
					setData(res.data);
					if (onData) {
						/***
						 * Consumer must parse response and trigger onSuccess / onError himself
						 */
						onData(res.data, merged);
					} else if (onSuccess) {
						/***
						 * Automatically onSuccess
						 */
						onSuccess(res.data);
					}
					resolve(res.data);
				})
				.catch((err) => {
					const errData = err && err.response ? err.response.data : undefined;
					setError(errData);
					if (onError) {
						/***
						 * callback when communication error
						 */
						onError(errData, merged);
					}
					reject(errData);
				})
				.finally(() => {
					setLoading(false);
				});
		});
	}, []);

	return [
		request,
		{
			data,
			loading,
			error,
		},
	];
}

export interface DataRequestArgs {
	context?: DataContextEmittedValues;
	domain?: string;
	xAPIKey?: string;
}

export function useDataRequest(args?: DataRequestArgs) {
	const { domain: defaultDomain, domains, getClient } = args && args.context ? args.context : useContext(DataContext);
	if (!args) args = {};
	if (!args.domain) {
		args.domain = defaultDomain;
	}

	const decorateOptions = <T = QueryHookOptions<any, any>>(options: T, args: DataRequestArgs) => {
		let newOptions = (options || {}) as T;
		if (args && args.xAPIKey) {
			newOptions = _set<T>(newOptions as unknown as object, 'headers.x-api-key', args.xAPIKey);
		}
		if (args && args.domain) {
			if (getClient) {
				const client = getClient(args.domain);
				newOptions = _set<T>(newOptions as unknown as object, 'client', client);
			}
		}
		return newOptions;
	};

	const useRestRequest: typeof BaseUseRestRequest = useCallback(
		(hookArgs) => {
			let newArgs = (hookArgs || {}) as RestArgs;
			newArgs.domains = domains;
			if (args && args.context) {
				newArgs = { ...newArgs, context: args.context };
			}
			if (args && args.xAPIKey) {
				newArgs = { ...newArgs, xAPIKey: args.xAPIKey };
			}
			if (args && args.domain) {
				newArgs = { ...newArgs, domain: args.domain };
			}
			return BaseUseRestRequest(newArgs);
		},
		[args]
	);

	const useQuery: typeof useQueryApollo = useCallback(
		(query, options) => {
			return useQueryApollo(query, decorateOptions<QueryHookOptions<any, any>>(options || {}, args || {}));
		},
		[args]
	);

	const useLazyQuery: typeof useLazyQueryApollo = useCallback(
		(query, options) => {
			return useLazyQueryApollo(
				query,
				decorateOptions<LazyQueryHookOptions<any, any>>(options || {}, args || {})
			);
		},
		[args]
	);

	const useMutation: typeof useMutationApollo = useCallback(
		(mutation, options) => {
			return useMutationApollo(
				mutation,
				decorateOptions<MutationHookOptions<any, any, any, any>>(options || {}, args || {})
			);
		},
		[args]
	);

	return {
		useRestRequest,
		useQuery,
		useLazyQuery,
		useMutation,
	};
}
